import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VoteGroupService, ElectionService, VoteService } from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';
import { VoteGroup, Election } from '../types';
import CandidatePhoto from '../components/CandidatePhoto';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ShieldCheckIcon,
  HomeIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface VoteChoice {
  electionId: string;
  electionTitle: string;
  electionPosition: string;
  candidateId: string | null;
  candidateName: string;
  candidatePhoto?: string;
  isAbstention: boolean;
  comment?: string;
  votedAt: Date;
}

const VoteChoicesPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { member, isMember } = useAuth();
  
  const [voteGroup, setVoteGroup] = useState<VoteGroup | null>(null);
  const [voteChoices, setVoteChoices] = useState<VoteChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadVoteChoices = async () => {
      if (!groupId) return;
      
      // Vérifier si l'utilisateur est connecté en tant que membre
      if (!isMember || !member) {
        setError('Vous devez être connecté en tant que membre pour voir vos votes');
        setLoading(false);
        return;
      }
      
      try {
        // Charger le groupe d'élections
        const groupData = await VoteGroupService.getVoteGroupById(groupId);
        if (!groupData) {
          setError('Groupe d\'élections introuvable');
          return;
        }
        setVoteGroup(groupData);

        // Charger les élections du groupe
        const electionsData = await ElectionService.getAllElections();
        const groupElections = groupData.elections
          .map(electionId => electionsData.find(e => e.id === electionId))
          .filter(Boolean) as Election[];

        // Récupérer les votes du membre pour ce groupe
        const memberVotes = await VoteService.getVotesByGroup(groupId);
        const memberVotesForGroup = memberVotes.filter(vote => vote.memberId === member.email);

        // Créer les choix de vote basés sur les vraies données
        const choices: VoteChoice[] = groupElections.map((election) => {
          // Chercher le vote du membre pour cette élection
          const memberVote = memberVotesForGroup.find(vote => vote.electionId === election.id);
          
          if (!memberVote) {
            // Pas encore voté
            return {
              electionId: election.id,
              electionTitle: election.title,
              electionPosition: election.position,
              candidateId: null,
              candidateName: 'Pas encore voté',
              isAbstention: false,
              votedAt: new Date()
            };
          }

          if (memberVote.isAbstention) {
            // Abstention
            return {
              electionId: election.id,
              electionTitle: election.title,
              electionPosition: election.position,
              candidateId: null,
              candidateName: 'Abstention',
              isAbstention: true,
              comment: memberVote.comment,
              votedAt: memberVote.timestamp
            };
          }

          // Vote pour un candidat
          const candidate = election.candidates.find(c => c.id === memberVote.candidateId);
          return {
            electionId: election.id,
            electionTitle: election.title,
            electionPosition: election.position,
            candidateId: memberVote.candidateId || null,
            candidateName: candidate?.name || 'Candidat inconnu',
            candidatePhoto: candidate?.photo,
            isAbstention: false,
            comment: memberVote.comment,
            votedAt: memberVote.timestamp
          };
        });

        setVoteChoices(choices);

      } catch (error) {
        console.error('Erreur lors du chargement des choix:', error);
        setError('Erreur lors du chargement de vos choix de vote');
      } finally {
        setLoading(false);
      }
    };

    loadVoteChoices();
  }, [groupId, member, isMember]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl">Chargement de vos choix...</p>
        </div>
      </div>
    );
  }

  if (error || !voteGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg p-8 max-w-md mx-4">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error || 'Données introuvables'}</p>
          {error && error.includes('connecté') && (
            <p className="text-sm text-gray-500 mb-4">
              Veuillez utiliser un lien magique valide pour accéder à cette page.
            </p>
          )}
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Un membre a voté s'il a fait un choix (vote ou abstention) pour au moins une élection
  const hasVoted = voteChoices.some(choice => choice.candidateName !== 'Pas encore voté');
  const totalElections = voteChoices.length;
  // Compter les élections où le membre a fait un choix (vote ou abstention)
  const votedElections = voteChoices.filter(choice => choice.candidateName !== 'Pas encore voté').length;
  const abstentionCount = voteChoices.filter(choice => choice.isAbstention).length;
  const candidateVoteCount = voteChoices.filter(choice => choice.candidateName !== 'Pas encore voté' && !choice.isAbstention).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      {/* En-tête */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Vos choix de votes
              </h1>
              <p className="text-blue-100">{voteGroup.name}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center text-white mb-2">
                <ShieldCheckIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">Vote Sécurisé</span>
              </div>
              <div className="text-blue-100 text-sm">
                {votedElections}/{totalElections} élection(s) votée(s)
                {abstentionCount > 0 && (
                  <span className="ml-2 text-orange-200">
                    ({abstentionCount} abstention{abstentionCount > 1 ? 's' : ''})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {hasVoted ? (
          <>
            {/* Message de confirmation */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <div className="flex items-start">
                <CheckCircleIcon className="w-6 h-6 text-green-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    Vous avez déjà voté
                  </h3>
                  <p className="text-green-800">
                    Vos votes ont été enregistrés de manière sécurisée. 
                    Vous ne pouvez plus modifier vos choix.
                  </p>
                </div>
              </div>
            </div>

            {/* Récapitulatif des votes */}
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden mb-8">
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-6">
                <h2 className="text-2xl font-bold text-white">
                  Récapitulatif de vos votes
                </h2>
                <p className="text-green-100">
                  {votedElections} élection(s) votée(s) - {candidateVoteCount} vote(s) pour candidat(s), {abstentionCount} abstention(s)
                </p>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  {voteChoices.map((choice, index) => (
                    <div key={choice.electionId} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-3">
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">
                              {index + 1}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {choice.electionTitle}
                              </h3>
                              <p className="text-sm text-gray-600">{choice.electionPosition}</p>
                            </div>
                          </div>

                          {/* Choix du candidat */}
                          <div className="ml-11">
                            {choice.candidateName === 'Pas encore voté' ? (
                              <div className="flex items-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <XCircleIcon className="w-6 h-6 text-gray-400 mr-3" />
                                <div>
                                  <p className="font-medium text-gray-600">Pas encore voté</p>
                                  <p className="text-sm text-gray-500">Vous n'avez pas encore voté pour cette élection</p>
                                </div>
                              </div>
                            ) : choice.isAbstention ? (
                              <div className="flex items-center p-4 bg-orange-50 border border-orange-200 rounded-lg">
                                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mr-4">
                                  <XCircleIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="font-medium text-orange-900">Abstention</p>
                                  <p className="text-sm text-orange-700">Vous vous êtes abstenu pour cette élection</p>
                                  <p className="text-xs text-orange-600 mt-1">
                                    Voté le {choice.votedAt.toLocaleDateString('fr-FR')} à {choice.votedAt.toLocaleTimeString('fr-FR')}
                                  </p>
                                  {choice.comment && (
                                    <div className="mt-3 p-3 bg-white border border-orange-200 rounded">
                                      <p className="text-xs font-medium text-orange-900 mb-1">Commentaire :</p>
                                      <p className="text-sm text-orange-800">{choice.comment}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
                                <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3" />
                                <div className="flex-1">
                                  <div className="flex items-center">
                                    <div className="mr-4">
                                      <CandidatePhoto 
                                        photo={choice.candidatePhoto} 
                                        name={choice.candidateName} 
                                        size="md" 
                                        className="w-12 h-12"
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-medium text-green-900">{choice.candidateName}</p>
                                      <p className="text-sm text-green-700">Votre choix pour cette élection</p>
                                      <p className="text-xs text-green-600 mt-1">
                                        Voté le {choice.votedAt.toLocaleDateString('fr-FR')} à {choice.votedAt.toLocaleTimeString('fr-FR')}
                                      </p>
                                      {choice.comment && (
                                        <div className="mt-3 p-3 bg-white border border-green-200 rounded">
                                          <p className="text-xs font-medium text-green-900 mb-1">Commentaire :</p>
                                          <p className="text-sm text-green-800">{choice.comment}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Message si pas encore voté */
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white">
                Vous n'avez pas encore voté
              </h2>
              <p className="text-blue-100">
                Cliquez sur le bouton ci-dessous pour commencer à voter
              </p>
            </div>
            <div className="p-8 text-center">
              <button
                onClick={() => navigate(`/vote/group/${groupId}`)}
                className="btn-primary text-lg px-8 py-4"
              >
                Commencer à voter
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
          >
            <HomeIcon className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </button>

          {!hasVoted && (
            <button
              onClick={() => navigate(`/vote/group/${groupId}`)}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Commencer à voter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoteChoicesPage;
